import { Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentElementResponse, Metadata } from 'src/openapi-client';

// src: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
function deepFreeze<T extends object>(object: T): T {
  const propNames = Reflect.ownKeys(object);

  for (const name of propNames) {
    const value = object[name];

    if ((value && typeof value === 'object') || typeof value === 'function') {
      deepFreeze(value);
    }
  }

  return Object.freeze(object);
}

@Injectable()
export class ElementContext implements OnDestroy {
  public loading!: Signal<boolean>;

  public get metadata(): Signal<Metadata | undefined | null> {
    return this._metadata;
  }

  private readonly elements: Map<string, ReplaySubject<DocumentElementResponse>> = new Map();
  private readonly _metadata: WritableSignal<Metadata | undefined | null> = signal(undefined);


  public getElement(elementName: string): Observable<DocumentElementResponse | undefined> {
    return this.elements.get(elementName)?.asObservable() ?? this.updateElement(elementName, undefined);
  }

  public setElementsReadonly(elements: DocumentElementResponse[]): void {
    for (const element of elements) {
      deepFreeze(element);
      this.updateElement(element.element.name, element);
    }
  }

  public ngOnDestroy(): void {
    for (const element of this.elements.values()) {
      element.complete();
    }
  }

  /**
   * @param metadata New value for metadata. `undefined` for pending metadata. `null` for no metadata.
   */
  public setMetadata(metadata: Metadata | null): void {
    this._metadata.set(metadata);
  }

  public clear(): void {
    for (const element of this.elements.values()) {
      element.next(undefined);
    }
  }

  private updateElement(
    elementName: string,
    value: DocumentElementResponse | undefined
  ): ReplaySubject<DocumentElementResponse> {
    let element: ReplaySubject<DocumentElementResponse> | undefined = this.elements.get(elementName);

    if (!element) {
      element = new ReplaySubject(1);
      this.elements.set(elementName, element);
    }

    if (value !== undefined) {
      element.next(value);
    }

    return element;
  }
}
